import { Component, inject } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import { MatDividerModule } from '@angular/material/divider';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { wideModalConfig } from '../../../consts/modal-config.const';
import { ApplyToEstImagePayModalComponent } from './apply-to-estimage-pay-modal.component';
import { StatusChipComponent } from '../../../components/status-chip/status-chip.component';
import { HumanizePipe } from '../../../pipes/humanize.pipe';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EstimagePayApplicationStore } from './estimage-pay-application.store';
import { DynamicDialogService } from '../../../components/dynamic-dialog/dynamic-dialog-service';

@Component({
  selector: 'estimage-pay-application',
  imports: [
    CommonModule,
    MatDividerModule,
    MatButtonModule,
    MatIconModule,
    StatusChipComponent,
    HumanizePipe,
    NgOptimizedImage,
    MatProgressSpinnerModule
  ],
  providers: [EstimagePayApplicationStore],
  template: `
    @if (estimagePayApplicationStore.isLoading()) {
      <div class="flex justify-center py-20">
        <mat-spinner diameter="40" />
      </div>
    } @else {
      <section class="space-y-20">
        <div class="flex justify-between items-end">
          <img
            [width]="340"
            [height]="105"
            ngSrc="assets/images/estimage_pay_banner.svg"
            alt="EstImage Pay Banner"
            priority />
          @if (!estimagePayApplicationStore.status()) {
            <button matButton="elevated" class="charcoal-button" (click)="applyToEstImagePay()">
              APPLY TO USE ESTIMAGE PAY
            </button>
          } @else {
            <status-chip [status]="estimagePayApplicationStore.status()" />
          }
        </div>
        <mat-divider />
        <div class="grid grid-cols-[1fr_auto] gap-20 mt-20">
          <div class="flex flex-col gap-y-20 px-15 subtitle-2">
            @if (!estimagePayApplicationStore.status()) {
              <div>
                EstImage Pay is an on-demand payment and finance facility for repairers. It offers instant invoice
                authorisations and daily settlements, providing a streamlined, embedded solution for B2B payments.
              </div>

              <div>
                Learn more here:
                <a href="https://public.estimage.com/estimage-pay" target="_blank">
                  https://public.estimage.com/estimage-pay </a
                >.
              </div>
            } @else if (estimagePayApplicationStore.status() === 'APPROVED') {
              <div>You are approved to use EstImage Pay to request invoice financing.</div>
            } @else {
              <div>
                <div>
                  Your request for approval to use EstImage Pay is
                  {{ estimagePayApplicationStore.status() | lowercase | humanize }}.
                </div>

                <div>
                  You can access your request to complete the required form here:
                  <a [href]="estimagePayApplicationStore.onboardingUrl()">
                    {{ estimagePayApplicationStore.onboardingUrl() }}
                  </a>
                </div>
              </div>
            }

            <div>
              EstImage Pay is powered by FlipMoney, who can be reached for support at 1300 354 766 or by e-mail at
              support&#64;flipmoney.com.au.
            </div>
          </div>

          @if (estimagePayApplicationStore.status() === 'APPROVED' && estimagePayApplicationStore.flipMoneyId()) {
            <div>
              <div class="body-2">Merchant ID</div>
              <div class="subtitle-2">{{ estimagePayApplicationStore.flipMoneyId() }}</div>
            </div>
          }
        </div>
      </section>
    }
  `
})
export class EstimagePayApplicationComponent {
  estimagePayApplicationStore = inject(EstimagePayApplicationStore);

  #dynamicDialogService = inject(DynamicDialogService);

  applyToEstImagePay() {
    const dialogConfig = {
      ...wideModalConfig,
      data: {
        title: 'Apply to EstImage Pay',
        action: 'APPLY',
        displayClose: true,
        component: ApplyToEstImagePayModalComponent
      }
    };
    this.#dynamicDialogService
      .openDialog(dialogConfig)
      .afterClosed()
      .subscribe((email: string) => {
        if (email) {
          this.estimagePayApplicationStore.applyToEstImage(email);
        }
      });
  }
}
